import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { api, leaguesAPI } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Skeleton } from '@/components/ui/skeleton';
import { Trophy, Medal, Award, TrendingUp, History, Calendar, MapPin } from 'lucide-react';
import { formatDate, getInitials } from '@/lib/utils';
import { PlayerProfileDialog } from '@/components/PlayerProfileDialog';

const PODIUM_STYLES = {
  1: {
    ring: 'ring-yellow-400',
    badge: 'bg-gradient-to-br from-yellow-400 to-amber-500 text-white shadow-lg shadow-yellow-500/30',
    card: 'from-yellow-50 to-amber-50 dark:from-yellow-950/40 dark:to-amber-950/40 border-yellow-200 dark:border-yellow-900',
    height: 'md:min-h-48',
    icon: Trophy,
    label: '1°',
  },
  2: {
    ring: 'ring-slate-300',
    badge: 'bg-gradient-to-br from-slate-300 to-slate-500 text-white shadow-md shadow-slate-400/30',
    card: 'from-slate-50 to-slate-100 dark:from-slate-900/40 dark:to-slate-800/40 border-slate-200 dark:border-slate-700',
    height: 'md:min-h-40',
    icon: Medal,
    label: '2°',
  },
  3: {
    ring: 'ring-amber-600',
    badge: 'bg-gradient-to-br from-amber-600 to-orange-700 text-white shadow-md shadow-amber-700/30',
    card: 'from-orange-50 to-amber-50 dark:from-orange-950/40 dark:to-amber-950/40 border-orange-200 dark:border-orange-900',
    height: 'md:min-h-36',
    icon: Award,
    label: '3°',
  },
};

const PodiumCard = ({ entry, rank, onClick }) => {
  const style = PODIUM_STYLES[rank];
  const Icon = style.icon;
  return (
    <button
      type="button"
      onClick={onClick}
      className={`group relative flex flex-col items-center text-center rounded-2xl border bg-gradient-to-br ${style.card} ${style.height} px-1 sm:px-4 pt-10 pb-4 transition-all hover:-translate-y-1 hover:shadow-xl active:scale-95`}
    >
      <div className={`absolute -top-5 left-1/2 -translate-x-1/2 h-10 w-10 rounded-full flex items-center justify-center ${style.badge}`}>
        <Icon className="h-5 w-5" />
      </div>
      <Avatar className={`h-12 w-12 sm:h-16 sm:w-16 ring-4 ${style.ring} ring-offset-2 ring-offset-background mb-2`}>
        <AvatarImage src={entry.picture} />
        <AvatarFallback className="text-lg font-bold">{getInitials(entry.name)}</AvatarFallback>
      </Avatar>
      <p className="font-bold truncate w-full text-sm md:text-base" title={entry.name}>
        {entry.name}
      </p>
      {entry.nickname && (
        <p className="text-[11px] italic text-primary truncate w-full">«{entry.nickname}»</p>
      )}
      <div className="mt-auto pt-2">
        <p className="text-2xl md:text-3xl font-bold tabular-nums leading-none">
          {entry.points}
        </p>
        <p className="text-[10px] uppercase tracking-widest text-muted-foreground mt-0.5">
          punti
        </p>
        <p className="text-[10px] text-muted-foreground tabular-nums mt-1">
          {entry.matches}P · {entry.wins}V {entry.draws}P {entry.losses}S
        </p>
      </div>
    </button>
  );
};

const RankRow = ({ entry, maxPoints, onClick }) => {
  const pct = maxPoints > 0 ? Math.max(6, Math.round((entry.points / maxPoints) * 100)) : 0;
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-muted/60 active:bg-muted transition-colors text-left"
    >
      <span className="w-7 text-center font-bold text-muted-foreground text-sm tabular-nums">
        {entry.rank}
      </span>
      <Avatar className="h-9 w-9">
        <AvatarImage src={entry.picture} />
        <AvatarFallback className="text-xs">{getInitials(entry.name)}</AvatarFallback>
      </Avatar>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 flex-wrap">
          <p className="font-medium truncate text-sm">{entry.name}</p>
          {entry.nickname && (
            <span className="text-[11px] text-primary italic truncate">«{entry.nickname}»</span>
          )}
        </div>
        <div className="flex items-center gap-2 mt-1">
          <div className="flex-1 h-1.5 rounded-full bg-muted overflow-hidden">
            <div
              className="h-full rounded-full bg-gradient-to-r from-primary to-blue-400"
              style={{ width: `${pct}%` }}
            />
          </div>
          <span className="text-[11px] text-muted-foreground tabular-nums shrink-0">
            {entry.matches}P · {entry.wins}V {entry.draws}P {entry.losses}S
          </span>
        </div>
      </div>
      <div className="text-right shrink-0">
        <p className="text-lg font-bold tabular-nums leading-none">{entry.points}</p>
        <p className="text-[10px] uppercase tracking-widest text-muted-foreground">pt</p>
      </div>
    </button>
  );
};

const LeaderboardContent = ({ leaderboard, onProfile }) => {
  if (!leaderboard.length) {
    return (
      <Card>
        <CardContent className="p-8 text-center text-muted-foreground">
          <Trophy className="h-12 w-12 mx-auto mb-3 opacity-40" />
          <p className="font-medium">Nessuna classifica disponibile</p>
        </CardContent>
      </Card>
    );
  }

  const top3 = leaderboard.slice(0, 3);
  const rest = leaderboard.slice(3);
  const maxPoints = leaderboard[0]?.points || 0;
  const podiumOrder = top3.length === 3 ? [top3[1], top3[0], top3[2]] : top3;

  return (
    <div className="space-y-4">
      {top3.length > 0 && (
        <div className="grid grid-cols-3 gap-1 sm:gap-3 md:gap-4 pt-6 px-1">
          {podiumOrder.map((entry) => (
            <PodiumCard
              key={entry.user_id}
              entry={entry}
              rank={entry.rank}
              onClick={() => onProfile(entry.user_id)}
            />
          ))}
        </div>
      )}

      {rest.length > 0 && (
        <Card>
          <CardContent className="p-2 md:p-3">
            <div className="divide-y">
              {rest.map((entry) => (
                <RankRow
                  key={entry.user_id}
                  entry={entry}
                  maxPoints={maxPoints}
                  onClick={() => onProfile(entry.user_id)}
                />
              ))}
            </div>
          </CardContent>
        </Card>
      )}

    </div>
  );
};

export const LeagueLeaderboard = ({ leagueId }) => {
  const [loading, setLoading] = useState(true);
  const [leaderboard, setLeaderboard] = useState([]);
  const [seasons, setSeasons] = useState([]);
  const [seasonsError, setSeasonsError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [profileUserId, setProfileUserId] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      const [leaderboardRes, seasonsRes] = await Promise.allSettled([
          api.get(`/stats/league/${leagueId}/leaderboard`),
          leaguesAPI.getSeasons(leagueId),
        ]);
      if (leaderboardRes.status === 'fulfilled') {
        setLeaderboard(leaderboardRes.value.data.leaderboard || []);
      } else {
        console.error('Error fetching leaderboard:', leaderboardRes.reason);
      }
      if (seasonsRes.status === 'fulfilled') {
        setSeasons(seasonsRes.value.data.seasons || []);
        setSeasonsError(false);
      } else {
        console.error('Error fetching seasons:', seasonsRes.reason);
        setSeasonsError(true);
      }
      setLoading(false);
    };

    if (leagueId) fetchData();
  }, [leagueId, reloadKey]);

  if (loading) return <Skeleton className="h-72" />;

  return (
    <div className="space-y-6" data-testid="league-leaderboard">
      <section className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-heading text-xl font-bold flex items-center gap-2">
            <Trophy className="h-5 w-5 text-yellow-500" />
            Classifica attuale
          </h3>
          <span className="text-xs uppercase tracking-widest text-muted-foreground flex items-center gap-1">
            <TrendingUp className="h-3 w-3" />
            {leaderboard.length} {leaderboard.length === 1 ? 'giocatore' : 'giocatori'}
          </span>
        </div>
        <LeaderboardContent leaderboard={leaderboard} onProfile={setProfileUserId} />
      </section>

      {seasons.length > 0 && (
        <Card>
          <CardContent className="p-4 md:p-6">
            <h3 className="font-heading text-xl font-bold flex items-center gap-2 mb-2">
              <History className="h-5 w-5 text-primary" />
              Storico stagioni
            </h3>
            <Accordion type="single" collapsible>
              {seasons.map((season) => {
                const champion = season.leaderboard?.[0];
                return (
                  <AccordionItem key={season.season_id} value={season.season_id}>
                    <AccordionTrigger className="hover:no-underline">
                      <div>
                        <p className="font-semibold">{season.name}</p>
                        <p className="text-xs font-normal text-muted-foreground mt-1">
                          {formatDate(season.started_at)} – {formatDate(season.ended_at)}
                          {champion ? ` · 🏆 ${champion.name}` : ''}
                        </p>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="space-y-6 pt-2">
                      <div>
                        <h4 className="font-semibold mb-2">Classifica finale</h4>
                        <LeaderboardContent leaderboard={season.leaderboard || []} onProfile={setProfileUserId} />
                      </div>
                      <div>
                        <h4 className="font-semibold mb-2">Partite ({season.matches?.length || 0})</h4>
                        <div className="space-y-2">
                          {(season.matches || []).map((match) => (
                            <Link
                              key={match.match_id}
                              to={`/matches/${match.match_id}`}
                              className="flex items-center justify-between gap-3 rounded-lg border p-3 hover:border-primary/50"
                            >
                              <div className="min-w-0">
                                <p className="font-medium truncate">{match.title}</p>
                                <p className="text-xs text-muted-foreground flex flex-wrap gap-x-3 gap-y-1 mt-1">
                                  <span className="flex items-center gap-1"><Calendar className="h-3 w-3" />{formatDate(match.date_time)}</span>
                                  {match.location_name && <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{match.location_name}</span>}
                                </p>
                              </div>
                              {match.result && (
                                <span className="font-bold tabular-nums shrink-0">
                                  {match.result.team_a_score ?? 0} – {match.result.team_b_score ?? 0}
                                </span>
                              )}
                            </Link>
                          ))}
                          {!season.matches?.length && <p className="text-sm text-muted-foreground">Nessuna partita archiviata</p>}
                        </div>
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                );
              })}
            </Accordion>
          </CardContent>
        </Card>
      )}

      {seasonsError && (
        <Card>
          <CardContent className="p-4 flex items-center justify-between gap-3 text-sm text-muted-foreground">
            <span>Storico stagioni non disponibile.</span>
            <Button variant="outline" size="sm" onClick={() => setReloadKey((key) => key + 1)}>
              Riprova
            </Button>
          </CardContent>
        </Card>
      )}

      <PlayerProfileDialog
        userId={profileUserId}
        open={!!profileUserId}
        onOpenChange={(open) => !open && setProfileUserId(null)}
      />
    </div>
  );
};

export default LeagueLeaderboard;
